import React, { useEffect, useMemo, useState } from 'react';
import {
  CurrencyDollarIcon,
  PhoneIcon,
  ClockIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import {
  adminService,
  type AdminChargeResponse,
  type AdminChargeCallType,
} from '../../services/adminService';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/UI/Card';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Button from '../../components/UI/Button';
import toast from 'react-hot-toast';

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }).format(value || 0);

const formatDuration = (seconds: number) => {
  const mins = Math.floor((seconds || 0) / 60);
  const secs = (seconds || 0) % 60;
  return `${mins}m ${secs}s`;
};

const formatDurationLong = (seconds: number) => {
  const total = Math.max(0, Math.floor(seconds || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  return `${hours}h ${minutes}m ${secs}s`;
};

/**
 * What each bucket is, in the client's language — the table is useless without
 * this because "AI" and "Auto" both dial automatically and get confused daily.
 */
const CALL_TYPE_META: Record<
  AdminChargeCallType,
  { label: string; blurb: string; accent: string }
> = {
  ai: {
    label: 'AI Bot',
    blurb: 'Bot campaigns — the AI talks to the lead itself. No agent on the line.',
    accent: 'bg-primary/10 text-primary',
  },
  auto: {
    label: 'Auto Dialer',
    blurb: 'Human campaigns on auto-dial — system dials, connects answered calls to an agent.',
    accent: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  },
  manual: {
    label: 'Manual Dialer',
    blurb: 'Human campaigns where the agent picks each lead and dials it.',
    accent: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  },
  other: {
    label: 'Other',
    blurb: 'Calls on campaigns that are neither bot nor human (e.g. SMS campaigns with a callback).',
    accent: 'bg-muted text-muted-foreground',
  },
};

const COST_COLUMN_HELP: Array<{ label: string; detail: string }> = [
  {
    label: 'Voice',
    detail:
      'Telnyx per-leg call cost (call control / SIP / WebRTC / recording), synced onto each call record. Applies to all three call types.',
  },
  {
    label: 'Relay + STT',
    detail:
      'Telnyx Conversation Relay and speech recognition, billed per minute of AI conversation. Only AI Bot calls can incur this.',
  },
  {
    label: 'OpenAI',
    detail:
      'LLM tokens metered inside our app on every Custom AI call. Billed on our OpenAI account, not by Telnyx. AI Bot only.',
  },
  {
    label: 'ElevenLabs',
    detail:
      'Text-to-speech characters metered inside our app. Billed on our ElevenLabs account (BYOK), not by Telnyx. AI Bot only.',
  },
];

const ViewChargesPage: React.FC = () => {
  const defaultTo = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  }, []);

  const [fromDate, setFromDate] = useState(toDateInputValue(defaultFrom));
  const [toDate, setToDate] = useState(toDateInputValue(defaultTo));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [data, setData] = useState<AdminChargeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadCharges = async (
    isRefresh = false,
    override?: { fromDate?: string; toDate?: string }
  ) => {
    try {
      const effectiveFromDate = override?.fromDate ?? fromDate;
      const effectiveToDate = override?.toDate ?? toDate;
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const response = await adminService.getCharges({
        from: `${effectiveFromDate}T00:00:00.000Z`,
        to: `${effectiveToDate}T23:59:59.999Z`,
        limit: 100,
      });
      setData(response);
    } catch (err: any) {
      const message = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || 'Failed to load charges';
      setError(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void loadCharges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void loadCharges(true);
    }, 15 * 60 * 1000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">View Charges</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Track daily Telnyx call charges for admin reporting.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">From</label>
            <input type="date" className="input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">To</label>
            <input type="date" className="input" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <Button
            variant="outlined"
            onClick={() => {
              const today = toDateInputValue(new Date());
              setFromDate(today);
              setToDate(today);
              void loadCharges(true, { fromDate: today, toDate: today });
            }}
            disabled={refreshing}
          >
            Today
          </Button>
          <Button onClick={() => void loadCharges(true)} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Apply'}
          </Button>
          <Button
            variant="outlined"
            onClick={async () => {
              if (syncing) return;
              try {
                setSyncing(true);
                const result = await adminService.syncCharges({
                  from: `${fromDate}T00:00:00.000Z`,
                  to: `${toDate}T23:59:59.999Z`,
                  maxPages: 40,
                });
                const aiPart = result.aiUpdated ? `, ${result.aiUpdated} with AI cost` : '';
                const message = `Synced ${result.updated} calls${aiPart} (${result.unmatched} unmatched)`;
                if (result.truncated) {
                  toast.error(`${message}. Partial: narrow the date range and re-run.`);
                } else {
                  toast.success(message);
                }
                await loadCharges(true);
              } catch (err: any) {
                const message = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || 'Failed to sync charges';
                toast.error(message);
              } finally {
                setSyncing(false);
              }
            }}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync Telnyx'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-6 text-destructive">{error}</CardContent>
        </Card>
      ) : (
        <>
          {data?.billingSource === 'telnyx_usage_reports' && (
            <Card>
              <CardContent className="p-4 text-sm text-muted-foreground">
                Billing totals are sourced from Telnyx usage reports for this date range. Use Sync Telnyx to
                backfill per-call voice and AI cost onto individual call records.
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">All Telnyx Billing</p>
                    <p className="text-2xl font-semibold text-foreground">{formatCurrency(data?.overview.totalCost || 0)}</p>
                  </div>
                  <CurrencyDollarIcon className="h-6 w-6 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg cost / call</p>
                    <p className="text-2xl font-semibold text-foreground">
                      {formatCurrency(data?.overview.averageCostPerCall || 0)}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Telnyx total ÷ {data?.overview.totalCalls || 0} calls
                    </p>
                  </div>
                  <CurrencyDollarIcon className="h-6 w-6 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg cost / min</p>
                    <p className="text-2xl font-semibold text-foreground">
                      {formatCurrency(data?.overview.averageCostPerMinute || 0)}
                    </p>
                  </div>
                  <ClockIcon className="h-6 w-6 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Custom AI stack</p>
                    <p className="text-2xl font-semibold text-foreground">
                      {formatCurrency(
                        data?.providerMetering?.customAiStackCost ??
                          (data?.overview.aiCost || 0) +
                            (data?.overview.openaiCost || 0) +
                            (data?.overview.elevenlabsCost || 0)
                      )}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Avg / bot call{' '}
                      {formatCurrency(data?.providerMetering?.averageCustomAiCostPerCall || 0)}
                    </p>
                  </div>
                  <SparklesIcon className="h-6 w-6 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Telnyx Calls</p>
                    <p className="text-2xl font-semibold text-foreground">{data?.overview.totalCalls || 0}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Billable {data?.usageReport?.totalCompleted || 0}
                    </p>
                  </div>
                  <PhoneIcon className="h-6 w-6 text-primary" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Duration</p>
                    <p className="text-2xl font-semibold text-foreground">{formatDuration(data?.overview.totalDurationSeconds || 0)}</p>
                  </div>
                  <ClockIcon className="h-6 w-6 text-primary" />
                </div>
              </CardContent>
            </Card>
          </div>

          {data?.byCallType && (
            <Card>
              <CardHeader>
                <CardTitle>Cost by Call Type — AI vs Auto vs Manual</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Every call is attributed to one bucket using the campaign it ran on. Costs come
                  from the per-call values on our own call records, so this table answers &ldquo;what
                  does each dialing mode actually cost&rdquo; — it is not a copy of the Telnyx invoice.
                </p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-muted/50 text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Call Type</th>
                        <th className="px-4 py-3 text-right font-medium">Calls</th>
                        <th className="px-4 py-3 text-right font-medium">Connected</th>
                        <th className="px-4 py-3 text-right font-medium">Talk Time</th>
                        <th className="px-4 py-3 text-right font-medium">Avg Length</th>
                        <th className="px-4 py-3 text-right font-medium">Voice</th>
                        <th className="px-4 py-3 text-right font-medium">Relay + STT</th>
                        <th className="px-4 py-3 text-right font-medium">OpenAI</th>
                        <th className="px-4 py-3 text-right font-medium">ElevenLabs</th>
                        <th className="px-4 py-3 text-right font-medium">Deepgram</th>
                        <th className="px-4 py-3 text-right font-medium">Total</th>
                        <th className="px-4 py-3 text-right font-medium">Avg / Call</th>
                        <th className="px-4 py-3 text-right font-medium">Avg / Min</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byCallType.rows.map((row) => {
                        const meta = CALL_TYPE_META[row.callType];
                        return (
                          <tr key={`ct-${row.callType}`} className="border-t border-border align-top">
                            <td className="px-4 py-3">
                              <span
                                className={`rounded px-2 py-0.5 text-xs font-medium ${meta.accent}`}
                              >
                                {meta.label}
                              </span>
                              <p className="mt-1.5 max-w-[16rem] text-xs text-muted-foreground">
                                {meta.blurb}
                              </p>
                            </td>
                            <td className="px-4 py-3 text-right">{row.totalCalls.toLocaleString()}</td>
                            <td className="px-4 py-3 text-right">
                              {row.connectedCalls.toLocaleString()}
                              {row.totalCalls > 0 && (
                                <span className="block text-xs text-muted-foreground">
                                  {Math.round((row.connectedCalls / row.totalCalls) * 100)}%
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {formatDurationLong(row.totalDurationSeconds)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {formatDuration(Math.round(row.averageDurationSeconds))}
                            </td>
                            <td className="px-4 py-3 text-right">{formatCurrency(row.voiceCost)}</td>
                            <td className="px-4 py-3 text-right">
                              {row.callType === 'ai' ? formatCurrency(row.relayCost) : '—'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {row.callType === 'ai' ? formatCurrency(row.llmCost) : '—'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {row.callType === 'ai' ? formatCurrency(row.ttsCost) : '—'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {row.callType === 'ai' ? formatCurrency(row.sttCost ?? 0) : '—'}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold">
                              {formatCurrency(row.totalCost)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {formatCurrency(row.averageCostPerCall)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {formatCurrency(row.averageCostPerMinute)}
                            </td>
                          </tr>
                        );
                      })}
                      {data.byCallType.rows.length === 0 && (
                        <tr>
                          <td className="px-4 py-6 text-muted-foreground" colSpan={13}>
                            No calls in selected date range.
                          </td>
                        </tr>
                      )}
                      <tr className="border-t border-border bg-muted/50 font-semibold">
                        <td className="px-4 py-3">Attributed total</td>
                        <td className="px-4 py-3 text-right">
                          {data.byCallType.attributedTotalCalls.toLocaleString()}
                        </td>
                        <td className="px-4 py-3" colSpan={8} />
                        <td className="px-4 py-3 text-right">
                          {formatCurrency(data.byCallType.attributedTotalCost)}
                        </td>
                        <td className="px-4 py-3" colSpan={2} />
                      </tr>
                      {data.byCallType.unattributedCost > 0 && (
                        <tr className="border-t border-border text-muted-foreground">
                          <td className="px-4 py-3" colSpan={10}>
                            Not attributable to a call type
                            <span className="block text-xs">
                              Account-wide Telnyx usage (Conversation Relay settlement, media
                              streaming, AMD, number rentals) that carries no campaign id. Run
                              <strong> Sync Telnyx</strong> to push more of this onto individual calls.
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {formatCurrency(data.byCallType.unattributedCost)}
                          </td>
                          <td className="px-4 py-3" colSpan={2} />
                        </tr>
                      )}
                      <tr className="border-t border-border bg-muted/30 font-semibold">
                        <td className="px-4 py-3" colSpan={10}>All Telnyx billing (account total)</td>
                        <td className="px-4 py-3 text-right">
                          {formatCurrency(data.byCallType.accountTotalCost)}
                        </td>
                        <td className="px-4 py-3" colSpan={2} />
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="border-t border-border p-4 text-xs text-muted-foreground space-y-3">
                  <div>
                    <p className="mb-1.5 font-medium text-foreground">What each cost column means</p>
                    <ul className="space-y-1">
                      {COST_COLUMN_HELP.map((item) => (
                        <li key={item.label}>
                          <span className="font-medium text-foreground">{item.label}</span> —{' '}
                          {item.detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="mb-1.5 font-medium text-foreground">How to read this</p>
                    <ul className="space-y-1">
                      <li>
                        <span className="font-medium text-foreground">Connected</span> counts calls
                        with talk time above zero. The percentage under it is the connect rate — the
                        number to watch when auditing the auto-dialer&apos;s cancellation problem.
                      </li>
                      <li>
                        <span className="font-medium text-foreground">Avg / Call</span> spreads cost
                        over every attempt including unanswered ones, so it is the true cost of a
                        dial. <span className="font-medium text-foreground">Avg / Min</span> only
                        counts connected talk time, so it is the right number for comparing AI
                        against a human agent.
                      </li>
                      <li>
                        Relay, OpenAI and ElevenLabs show &ldquo;—&rdquo; for Auto and Manual because
                        those calls never touch the AI stack. Only Voice applies to them.
                      </li>
                    </ul>
                  </div>
                  {!data.byCallType.columnsReady && (
                    <p className="text-warning">
                      Per-call AI cost columns are missing on this database — Relay / OpenAI /
                      ElevenLabs will read $0. Run the 093 and 094 migrations to enable them.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {(data?.aiUsage || data?.providerMetering) && (
            <Card>
              <CardHeader>
                <CardTitle>Custom AI Cost Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-muted/50 text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Product</th>
                        <th className="px-4 py-3 text-left font-medium">Source</th>
                        <th className="px-4 py-3 text-left font-medium">Usage</th>
                        <th className="px-4 py-3 text-left font-medium">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.aiUsage?.products || []).map((row) => (
                        <tr key={`ai-${row.key}`} className="border-t border-border">
                          <td className="px-4 py-3">{row.product}</td>
                          <td className="px-4 py-3">
                            <span
                              className={
                                row.category === 'ai'
                                  ? 'rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary'
                                  : 'rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'
                              }
                            >
                              {row.category === 'ai' ? 'Telnyx AI' : 'Telnyx shared'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">—</td>
                          <td className="px-4 py-3 font-medium">{formatCurrency(row.cost)}</td>
                        </tr>
                      ))}
                      {data.providerMetering && (
                        <>
                          <tr className="border-t border-border">
                            <td className="px-4 py-3">OpenAI (LLM)</td>
                            <td className="px-4 py-3">
                              <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                Metered
                              </span>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {data.providerMetering.openai.promptTokens.toLocaleString()} in /{' '}
                              {data.providerMetering.openai.completionTokens.toLocaleString()} out
                              {' · '}
                              {data.providerMetering.openai.meteredCalls} calls
                            </td>
                            <td className="px-4 py-3 font-medium">
                              {formatCurrency(data.providerMetering.openai.cost)}
                            </td>
                          </tr>
                          <tr className="border-t border-border">
                            <td className="px-4 py-3">ElevenLabs (TTS)</td>
                            <td className="px-4 py-3">
                              <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                Metered
                              </span>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {data.providerMetering.elevenlabs.characters.toLocaleString()} chars
                              {' · '}
                              {data.providerMetering.elevenlabs.meteredCalls} calls
                            </td>
                            <td className="px-4 py-3 font-medium">
                              {formatCurrency(data.providerMetering.elevenlabs.cost)}
                            </td>
                          </tr>
                          {/* Self-hosted engine only — Conversation Relay bundles
                              transcription into the Telnyx relay minute above. */}
                          <tr className="border-t border-border">
                            <td className="px-4 py-3">Deepgram (STT)</td>
                            <td className="px-4 py-3">
                              <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                Metered
                              </span>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {formatDurationLong(data.providerMetering.deepgram?.seconds || 0)}
                              {' · '}
                              {data.providerMetering.deepgram?.meteredCalls || 0} calls
                            </td>
                            <td className="px-4 py-3 font-medium">
                              {formatCurrency(data.providerMetering.deepgram?.cost || 0)}
                            </td>
                          </tr>
                        </>
                      )}
                      {!data.aiUsage?.products?.length && !data.providerMetering && (
                        <tr>
                          <td className="px-4 py-6 text-muted-foreground" colSpan={4}>
                            No AI product usage in selected date range.
                          </td>
                        </tr>
                      )}
                      <tr className="border-t border-border bg-muted/30 font-medium">
                        <td className="px-4 py-3" colSpan={3}>Telnyx AI pipeline (Conversation Relay + STT)</td>
                        <td className="px-4 py-3">{formatCurrency(data.aiUsage?.totals.aiCost || 0)}</td>
                      </tr>
                      <tr className="border-t border-border bg-muted/30 font-medium">
                        <td className="px-4 py-3" colSpan={3}>
                          OpenAI + ElevenLabs + Deepgram (metered)
                        </td>
                        <td className="px-4 py-3">
                          {formatCurrency(
                            (data.providerMetering?.openai.cost || 0) +
                              (data.providerMetering?.elevenlabs.cost || 0) +
                              (data.providerMetering?.deepgram?.cost || 0)
                          )}
                        </td>
                      </tr>
                      <tr className="border-t border-border bg-muted/50 font-semibold">
                        <td className="px-4 py-3" colSpan={3}>Custom AI stack total</td>
                        <td className="px-4 py-3">
                          {formatCurrency(data.providerMetering?.customAiStackCost || data.aiUsage?.totals.aiCost || 0)}
                        </td>
                      </tr>
                      <tr className="border-t border-border bg-muted/30 font-medium">
                        <td className="px-4 py-3" colSpan={3}>
                          Avg Custom AI cost / bot call
                          {data.providerMetering?.botCalls
                            ? ` (${data.providerMetering.botCalls} bot calls)`
                            : ''}
                        </td>
                        <td className="px-4 py-3">
                          {formatCurrency(data.providerMetering?.averageCustomAiCostPerCall || 0)}
                        </td>
                      </tr>
                      <tr className="border-t border-border text-muted-foreground">
                        <td className="px-4 py-3" colSpan={3}>
                          Avg OpenAI / metered call
                          {data.providerMetering?.openai.meteredCalls
                            ? ` (${data.providerMetering.openai.meteredCalls})`
                            : ''}
                          {' · '}
                          Avg ElevenLabs / metered call
                          {data.providerMetering?.elevenlabs.meteredCalls
                            ? ` (${data.providerMetering.elevenlabs.meteredCalls})`
                            : ''}
                          {' · '}
                          Avg Deepgram / metered call
                          {data.providerMetering?.deepgram?.meteredCalls
                            ? ` (${data.providerMetering.deepgram.meteredCalls})`
                            : ''}
                        </td>
                        <td className="px-4 py-3">
                          {formatCurrency(data.providerMetering?.averageOpenaiCostPerCall || 0)}
                          {' / '}
                          {formatCurrency(data.providerMetering?.averageElevenlabsCostPerCall || 0)}
                          {' / '}
                          {formatCurrency(data.providerMetering?.averageDeepgramCostPerCall || 0)}
                        </td>
                      </tr>
                      <tr className="border-t border-border bg-muted/30 font-medium">
                        <td className="px-4 py-3" colSpan={3}>Telnyx shared (AMD, media streaming)</td>
                        <td className="px-4 py-3">{formatCurrency(data.aiUsage?.totals.sharedCost || 0)}</td>
                      </tr>
                      <tr className="border-t border-border bg-muted/50 font-semibold">
                        <td className="px-4 py-3" colSpan={3}>All Telnyx billing</td>
                        <td className="px-4 py-3">{formatCurrency(data.overview.totalCost || 0)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="border-t border-border p-4 text-xs text-muted-foreground space-y-1">
                  <p>
                    Conversation Relay (Telnyx) covers speech recognition and voice orchestration.
                    OpenAI and ElevenLabs are metered inside our app from each Custom AI call going
                    forward — older calls in this range will show $0 until new traffic is captured.
                  </p>
                  <p>
                    Deepgram appears only for calls on the self-hosted engine, which transcribes on
                    our own key and bills per second. Relay calls transcribe inside the Telnyx AI
                    pipeline row above, so a $0 Deepgram line means no self-hosted traffic in this
                    range — not missing data.
                  </p>
                  {data.aiUsage?.scope === 'account' && (
                    <p>
                      Telnyx Conversation Relay spend is account-wide (no campaign filter). OpenAI /
                      ElevenLabs sums follow the selected owner&apos;s bot campaigns.
                    </p>
                  )}
                  {data.providerMetering && !data.providerMetering.columnsReady && (
                    <p className="text-warning">
                      Metering columns are missing — run migrations 094 (LLM/TTS) and 095 (STT) on
                      the database.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Daily Charges</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Date</th>
                      <th className="px-4 py-3 text-left font-medium">Calls</th>
                      <th className="px-4 py-3 text-left font-medium">Charged</th>
                      <th className="px-4 py-3 text-left font-medium">Duration</th>
                      <th className="px-4 py-3 text-left font-medium">AI Cost</th>
                      <th className="px-4 py-3 text-left font-medium">Total Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.daily || []).map((row) => (
                      <tr key={`${row.day}`} className="border-t border-border">
                        <td className="px-4 py-3">{new Date(row.day).toLocaleDateString()}</td>
                        <td className="px-4 py-3">{row.totalCalls}</td>
                        <td className="px-4 py-3">{row.chargedCalls}</td>
                        <td className="px-4 py-3">{formatDuration(row.totalDurationSeconds)}</td>
                        <td className="px-4 py-3">{formatCurrency(row.aiCost || 0)}</td>
                        <td className="px-4 py-3 font-medium">{formatCurrency(row.totalCost)}</td>
                      </tr>
                    ))}
                    {(data?.daily || []).length === 0 && (
                      <tr>
                        <td className="px-4 py-6 text-muted-foreground" colSpan={6}>No data in selected date range.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {data?.portalUsage && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Inbound Usage (Telnyx Reporting)</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-muted/50 text-muted-foreground">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium">Product</th>
                          <th className="px-4 py-3 text-left font-medium">Number of Calls</th>
                          <th className="px-4 py-3 text-left font-medium">Billable Calls</th>
                          <th className="px-4 py-3 text-left font-medium">Billed Duration</th>
                          <th className="px-4 py-3 text-left font-medium">ACD</th>
                          <th className="px-4 py-3 text-left font-medium">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.portalUsage.inbound.map((row) => (
                          <tr key={`in-${row.product}`} className="border-t border-border">
                            <td className="px-4 py-3">{row.product}</td>
                            <td className="px-4 py-3">{row.attemptedCalls}</td>
                            <td className="px-4 py-3">{row.billableCalls}</td>
                            <td className="px-4 py-3">{formatDurationLong(row.billedSeconds)}</td>
                            <td className="px-4 py-3">{formatDuration(Math.round(row.averageCallDurationSeconds || 0))}</td>
                            <td className="px-4 py-3 font-medium">{formatCurrency(row.cost)}</td>
                          </tr>
                        ))}
                        <tr className="border-t border-border bg-muted/30 font-medium">
                          <td className="px-4 py-3" colSpan={5}>Inbound Total</td>
                          <td className="px-4 py-3">{formatCurrency(data.portalUsage.totals.inboundCost)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Outbound Usage (Telnyx Reporting)</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-muted/50 text-muted-foreground">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium">Product</th>
                          <th className="px-4 py-3 text-left font-medium">Number of Calls</th>
                          <th className="px-4 py-3 text-left font-medium">Billable Calls</th>
                          <th className="px-4 py-3 text-left font-medium">Billed Duration</th>
                          <th className="px-4 py-3 text-left font-medium">ACD</th>
                          <th className="px-4 py-3 text-left font-medium">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.portalUsage.outbound.map((row) => (
                          <tr key={`out-${row.product}`} className="border-t border-border">
                            <td className="px-4 py-3">{row.product}</td>
                            <td className="px-4 py-3">{row.attemptedCalls}</td>
                            <td className="px-4 py-3">{row.billableCalls}</td>
                            <td className="px-4 py-3">{formatDurationLong(row.billedSeconds)}</td>
                            <td className="px-4 py-3">{formatDuration(Math.round(row.averageCallDurationSeconds || 0))}</td>
                            <td className="px-4 py-3 font-medium">{formatCurrency(row.cost)}</td>
                          </tr>
                        ))}
                        <tr className="border-t border-border bg-muted/30 font-medium">
                          <td className="px-4 py-3" colSpan={5}>Outbound Total</td>
                          <td className="px-4 py-3">{formatCurrency(data.portalUsage.totals.outboundCost)}</td>
                        </tr>
                        <tr className="border-t border-border bg-muted/50 font-semibold">
                          <td className="px-4 py-3" colSpan={5}>Voice products total (calls / SIP / WebRTC / recording)</td>
                          <td className="px-4 py-3">{formatCurrency(data.portalUsage.totals.overallCost)}</td>
                        </tr>
                        <tr className="border-t border-border bg-muted/50 font-semibold">
                          <td className="px-4 py-3" colSpan={5}>All Telnyx billing (voice + AI + shared)</td>
                          <td className="px-4 py-3">{formatCurrency(data.overview.totalCost || 0)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

        </>
      )}
    </div>
  );
};

export default ViewChargesPage;
